/**
 * @remarks
 * Vuex module for Data request
 **/
import { PortalAdapterApi } from "@vacancorp/portal.adapter.api";
import portal from "@vacancorp/portal";
import { ActionTree, MutationTree, GetterTree, Commit, Dispatch } from "vuex";
import { DataState } from "./@types/state-module";
import Api, { FilterParams, createFavoritePlace, deleteFavoritePlace } from "../api/adapter.api";
import { getLocalStorage, setLocalStorage } from "@/util/utils";
import i18n from "@/i18n";
import { distanceFromLatLng } from "@/mixins/common-functions";
import Vue from "vue";
import { AreaService } from "@/api/services/area.service";
import { PlaceService } from "@/api/services/place.service";
import { DEFAULT_AREA } from "@/models/area.model";
import { genreToMapsMapType, hasMapTypeMarkerGenre, MapsMapType } from "@/util/filter-genre.helper";

// local helper state to reduce noisy geolocation updates
let __lastGeoLocation: PortalAdapterApi.Location | undefined;
let __lastGeoUpdateAt = 0;
let __geoErrorCounts = 0;

function _getSelectedPlaceIdHash(): string | undefined {
  const selectedPlaceIdHash: string | null | undefined = getLocalStorage("selectedPlaceIdHash");
  return selectedPlaceIdHash === null || selectedPlaceIdHash === undefined ? undefined : selectedPlaceIdHash;
}

export const state: DataState = {
  location: {
    lat: undefined,
    lng: undefined,
  },
  isFirstLoadMap: undefined,
  watchingId: undefined,
  language: i18n.locale,
  placeList: [],
  sortedPlaceList: [],
  restaurantImagesList: [],
  displayVacancyList: [],
  showGPSDenyAlertByTimeout: false,
  hasCurrentLocation: undefined,
  selectedPlaceIdHash: _getSelectedPlaceIdHash(),
  selectedPlaceIdHashList: [],
  selectedSameBuilding: undefined,
  selectedSameBuildingPlaceIdHashList: [],
  isFirstSearch: true,
  currentPositionMarker: undefined,
  gMaps: undefined,
  gMapsReady: false,
  restaurantImageNumber: 0,
  vacancyStatusErrorTimes: 0,
  sameBuildingVacancyStatusErrorTimes: 0,
  announcementNumber: 0,
  readAnnouncementNumber: 0,
  selectedTopPlace: DEFAULT_AREA,
  selectedCountry: "jp",
  selectedPrefecture: "",
  genreList: [],
  selectedGenres: [],
  selectedGenreCategoryList: [],
  selectedFilters: [],
  mapsZoom: 13,
  isOpendata: false,
  mapsMapType: "normal",
};

export const mutations: MutationTree<DataState> = {
  setIsOpendata(state: DataState, payload: boolean) {
    state.isOpendata = payload;
  },
  setCurrentPositionMarker(state: DataState, payload?: google.maps.Marker) {
    state.currentPositionMarker = payload;
  },
  setIsFirstLoadMap(state: DataState, payload: boolean) {
    state.isFirstLoadMap = payload;
  },
  setShowGPSDenyAlertByTimeout(state: DataState, value: boolean) {
    state.showGPSDenyAlertByTimeout = value;
  },
  setMapsZoom(state: DataState, value: number) {
    state.mapsZoom = value;
  },
  setLanguage(state: DataState, payload: string) {
    state.language = payload;
    setLocalStorage("vacan/maps/language", payload);
  },
  setReadAnnouncementNumber(state: DataState, payload: number) {
    state.readAnnouncementNumber = payload;
  },
  setIsFirstSearch(state: DataState, payload: boolean) {
    state.isFirstSearch = payload;
  },
  setLocation(state: DataState, pos: PortalAdapterApi.Location | undefined) {
    if (pos !== undefined) {
      state.location.lat = pos.lat;
      state.location.lng = pos.lng;
    } else {
      state.location.lat = 0;
      state.location.lng = 0;
    }
    console.log(`--- setLocation ---`);
  },
  setAnnouncementNumber(state: DataState, number: number) {
    state.announcementNumber = number;
  },
  setLocationWatchingId(state: DataState, id: number) {
    state.watchingId = id;
  },
  setSelectedTopPlace: (state: DataState, selectedTopPlace: PortalAdapterApi.TopArea) => {
    state.selectedTopPlace = selectedTopPlace;
  },
  setSelectedCountry: (state: DataState, countryName: string) => {
    state.selectedCountry = countryName;
  },
  setSelectedPrefecture: (state: DataState, name: string) => {
    state.selectedPrefecture = name;
  },
  clearSelectedGenre: (state: DataState) => {
    state.selectedGenres = [];
  },
  // selected genre
  setSelectedGenres: (state: DataState, selectedGenres: (PortalAdapterApi.PlaceCategoryType | "all")[]) => {
    console.log(`setSelectedGenres: ${JSON.stringify(selectedGenres)}`);
    state.selectedGenres = selectedGenres;
    // Convert to mapsMapType automatically only for original genres
    // because some bookmarks were not included the new mapsMapType
    // "all" is the sentinel for the normal/日常マップ button, so it must also
    // re-derive mapsMapType back to "normal" (otherwise it stays stuck on
    // whatever non-normal type was previously selected)
    if (hasMapTypeMarkerGenre(selectedGenres) || selectedGenres.includes("all")) {
      state.mapsMapType = genreToMapsMapType(selectedGenres);
      console.log(`mapsMapType: ${state.mapsMapType}`);
    }
  },

  setMapsMapType: (state: DataState, payload: MapsMapType) => {
    state.mapsMapType = payload;
    console.log(`mapsMapType: ${state.mapsMapType}`);
  },

  // genre's category
  setSelectedGenreCategoryList: (state: DataState, payload?: string[]) => {
    state.selectedGenreCategoryList = payload === undefined ? [] : payload;
  },
  // quick filter : vacant, favorite
  setSelectedFilters: (state: DataState, filters: string[]) => {
    state.selectedFilters = filters;
  },
  setPlaceList: (state: DataState, placeList: PortalAdapterApi.ResponseGetPublicPlace[]) => {
    state.placeList = placeList;
  },
  setPlaceListIndex: (state: DataState, payload: { index: number; place: PortalAdapterApi.ResponseGetPublicPlace }) => {
    Vue.set(state.placeList, payload.index, payload.place);
  },
  setSortedPlaceList: (state: DataState, placeList: portal.PublicPlaceWithVacancyId[]) => {
    state.sortedPlaceList = placeList;
  },
  setSortedPlaceListIndex: (state: DataState, payload: { index: number; place: portal.PublicPlaceWithVacancyId }) => {
    Vue.set(state.sortedPlaceList, payload.index, payload.place);
  },
  setFacilityVacancyList(state: DataState, displayVacancyList: PortalAdapterApi.ResponseGetMergedDisplayVacancy[]) {
    state.displayVacancyList = displayVacancyList;
  },
  setRestaurantImagesList: (state: DataState, list: PortalAdapterApi.ResponseGetShopImage[]) => {
    state.restaurantImagesList = list;
  },
  hasCurrentLocation(state: DataState, payload: boolean) {
    state.hasCurrentLocation = payload;
  },
  setSelectedPlaceIdHash: (state: DataState, selectedPlaceIdHash: string) => {
    state.selectedPlaceIdHash = selectedPlaceIdHash;
    setLocalStorage("selectedPlaceIdHash", selectedPlaceIdHash);
  },
  setSelectedSameBuilding: (state: DataState, selectedBuilding: PortalAdapterApi.Building) => {
    state.selectedSameBuilding = selectedBuilding;
  },
  setSelectedPlaceIdHashList: (state: DataState, selectedPlaceIdHashList: string[]) => {
    state.selectedPlaceIdHashList = selectedPlaceIdHashList;
  },
  setSelectedSameBuildingPlaceIdHashList: (state: DataState, selectedPlaceIdHashList: string[]) => {
    state.selectedSameBuildingPlaceIdHashList = selectedPlaceIdHashList;
  },
  setRestaurantImageNumber(state: DataState, num: number) {
    state.restaurantImageNumber = num;
  },
  setGMaps(state: DataState, payload: google.maps.Map) {
    state.gMaps = payload;
  },
  setGMapsReady(state: DataState, payload: boolean) {
    state.gMapsReady = payload;
  },
  setVacancyStatusErrorTimes(state: DataState, payload: number) {
    state.vacancyStatusErrorTimes = payload;
  },
  setSameBuildingVacancyStatusErrorTimes(state: DataState, payload: number) {
    state.sameBuildingVacancyStatusErrorTimes = payload;
  },
};

export const actions: ActionTree<DataState, any> = {
  // direct access by areaName
  async getLocationByAreaName(
    { commit }: { commit: Commit },
    { areaName, mapsMapType, isOpendata }: { areaName: string; mapsMapType?: string; isOpendata?: boolean },
  ) {
    try {
      const result: PortalAdapterApi.TopArea | undefined = await AreaService.fetchAreaInfoByName({
        areaName,
        mapsMapType,
        isOpendata,
      });
      commit("setSelectedTopPlace", result);
    } catch {
      commit("setSelectedTopPlace", undefined);
    }
  },
  /** @remarks GPS location */
  checkGeolocation({ state, commit, dispatch }: { state: DataState; commit: Commit; dispatch: Dispatch }) {
    // check if user allow or deny location permission
    console.log(`--- dispatch checkGeolocation ---`);
    const gpsOption = { timeout: 8000, enableHighAccuracy: true, maximumAge: 5000 };

    if (window.navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        // success
        (pos) => {
          const location: PortalAdapterApi.Location = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          };
          commit("setLocation", location);
          commit("hasCurrentLocation", true);
          if (state.watchingId === undefined) {
            dispatch("startWatchingGeolocation");
          }
        },
        // error
        (error: any) => {
          switch (error.code) {
            case error.PERMISSION_DENIED:
              break;
            case error.POSITION_UNAVAILABLE:
              commit("setShowGPSDenyAlertByTimeout", true);
              break;
            case error.TIMEOUT:
              commit("setShowGPSDenyAlertByTimeout", true);
              break;
            case error.UNKNOWN_ERROR:
              commit("setShowGPSDenyAlertByTimeout", true);
              break;
          }
          console.error("checkGeolocation error");
          dispatch("endWatchingGeoLocation");
        },
        gpsOption,
      );
    } else {
      console.error("Browser is not supporting geolocation");
      dispatch("endWatchingGeoLocation");
    }
  },
  startWatchingGeolocation({ state, commit, dispatch }: { state: DataState; commit: Commit; dispatch: Dispatch }) {
    console.log(`--- startWatchingGeolocation ---`);
    // If already watching, do nothing
    if (state.watchingId !== undefined) {
      return;
    }
    const options: PositionOptions = { timeout: 20000, enableHighAccuracy: true, maximumAge: 5000 };
    const id: number = navigator.geolocation.watchPosition(
      (pos) => {
        const location: PortalAdapterApi.Location = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };
        // Throttle and ignore micro movements to avoid UI flicker
        const now = Date.now();
        const hasPrev = __lastGeoLocation !== undefined;
        const movedKm =
          hasPrev && __lastGeoLocation ? distanceFromLatLng(__lastGeoLocation, location) : Number.POSITIVE_INFINITY;
        const movedEnough = movedKm === Number.POSITIVE_INFINITY || movedKm > 0.01; // > 10 meters
        const timeOK = now - __lastGeoUpdateAt > 1000; // at most once per second

        if (movedEnough && timeOK) {
          __lastGeoLocation = location;
          __lastGeoUpdateAt = now;
          commit("setLocation", location);
          commit("hasCurrentLocation", true);
          __geoErrorCounts = 0;
        }
      },
      () => {
        console.error("watchGeolocation error");
        __geoErrorCounts += 1;
        if (__geoErrorCounts >= 5) {
          dispatch("endWatchingGeoLocation");
          __geoErrorCounts = 0;
        }
      },
      options,
    );
    commit("setLocationWatchingId", id);
  },
  endWatchingGeoLocation({ state, commit }: { state: DataState; commit: Commit }) {
    __geoErrorCounts = 0;
    commit("setLocationWatchingId", undefined);
    commit("hasCurrentLocation", false);
    if (state.watchingId === undefined) return;
    navigator.geolocation.clearWatch(state.watchingId);
  },
  /** @remarks about places */
  // Same building list
  getSameBuildingList({}: { state: DataState; commit: Commit }, buildingIdHash: string) {
    return new Promise(async (resolve, reject) => {
      try {
        const placeList: PortalAdapterApi.ResponseGetPublicPlace[] = await Api.fetchSameBuildingList(
          buildingIdHash,
        ).catch(() => {
          return Promise.reject([]);
        });

        if (placeList !== undefined) {
          resolve(placeList);
        } else {
          reject();
        }
      } catch (e) {
        reject();
      }
    }); // Promise
  },
  // 店舗リスト
  fetchPlace(
    {}: { state: DataState; commit: Commit },
    payload: {
      lat: number;
      lng: number;
      radius: number;
      countryName: string;
      params: FilterParams;
      areaName?: string;
      mapType?: string;
      isOpendata?: boolean;
    },
  ) {
    return new Promise(async (resolve, reject) => {
      try {
        const placeList: PortalAdapterApi.ResponseGetPublicPlace[] = await PlaceService.fetchPlaceListByLocation({
          lat: payload.lat,
          lng: payload.lng,
          radius: payload.radius,
          countryName: payload.countryName,
          params: payload.params,
          areaName: payload.areaName,
          mapType: payload.mapType,
          isOpendata: payload.isOpendata,
        }).catch(() => {
          return Promise.reject([]);
        });

        if (placeList !== undefined) {
          resolve(placeList);
        } else {
          reject();
        }
      } catch (e) {
        reject();
      }
    }); // Promise
  },
  // 詳細ページ
  getPlaceDetailByPlaceIdHash(
    {}: { state: DataState; commit: Commit },
    payload: { placeIdHash: string; isOpendata?: boolean },
  ) {
    return new Promise(async (resolve, reject) => {
      try {
        let result: PortalAdapterApi.ResponseGetPlaceDetail = await Api.fetchPlaceDetailByPlaceIdHash(
          payload.placeIdHash,
          payload.isOpendata,
        ).catch(() => Promise.reject({}));

        if (result.placeIdHash !== undefined) {
          resolve(result);
        } else {
          reject({});
        }
      } catch (e) {
        reject();
      }
    }); //Promise
  },
  // 店舗のcoupon
  getCouponListByPlaceIdHash({}: { state: DataState; commit: Commit }, payload: { placeIdHash: string }) {
    return new Promise(async (resolve, reject) => {
      try {
        const result: PortalAdapterApi.ResponseGetPublicCouponForPortal[] = await Api.fetchCouponListByPlaceIdHash(
          payload.placeIdHash,
        ).catch(() => {
          return Promise.reject([]);
        });
        if (result !== undefined) resolve(result);
        else reject([]);
      } catch (e) {
        reject();
      }
    });
  },
  // お知らせ
  getAnnouncementList() {
    return new Promise(async (resolve, reject) => {
      try {
        const result: PortalAdapterApi.ResponseGetNotice[] = await Api.fetchAnnouncementList().catch(() => {
          return Promise.reject([]);
        });
        if (result !== undefined) resolve(result);
        else reject([]);
      } catch (e) {
        reject();
      }
    });
  },
  getAnnouncementNumber() {
    return new Promise(async (resolve, reject) => {
      try {
        const result: number = await Api.fetchAnnouncementNumber().catch(() => {
          return Promise.reject([]);
        });
        if (!isNaN(result)) resolve(result);
        else reject(-1);
      } catch (e) {
        reject();
      }
    });
  },
  /** favorite 気に入り */
  async createNewFavorite({}: { state: DataState }, placeIdHash: string): Promise<boolean> {
    const isOpendata: boolean = state.placeList.find((place) => place.placeIdHash === placeIdHash)?.isOpendata ?? false;
    return await createFavoritePlace(placeIdHash, isOpendata).catch((err: any) => {
      console.error(`Create new favorite failed: ${err?.error}`);
      return true;
    });
  },
  async deleteFavorite({}: { state: DataState }, placeIdHash: string): Promise<boolean> {
    const isOpendata: boolean = state.placeList.find((place) => place.placeIdHash === placeIdHash)?.isOpendata ?? false;
    return await deleteFavoritePlace(placeIdHash, isOpendata).catch((err: any) => {
      console.error(`Delete favorite failed: ${err?.error}`);
      return true;
    });
  },
  /** @remarks get vacancy list of places and their facilities every 10 seconds */
  getPlaceFacilityVacancyList({}: { commit: Commit; state: DataState; dispatch: Dispatch }, placeIdHashList: string[]) {
    return new Promise(async (resolve, reject) => {
      const displayVacancyList: PortalAdapterApi.ResponseGetMergedDisplayVacancy[] | undefined =
        await PlaceService.fetchFacilityVacancyListByPlaceIdHashList(placeIdHashList).catch(() => {
          console.error(`catch getPlaceFacilityVacancyList in vuex`);
          return undefined;
        });

      if (displayVacancyList !== undefined) {
        resolve(displayVacancyList);
      } else {
        reject([]);
      }
      // ignore reject here because of in a loop
    });
  },
};

export const getters: GetterTree<DataState, any> = {
  isOpendataEvacuationCenter: (state: DataState) => {
    return state.isOpendata && state.selectedGenres?.includes("evacuation-center");
  },
  isNotNormalMap: (state: DataState) => {
    return state.mapsMapType !== "normal";
  },
  getSelectedCountry: (state: DataState) => {
    return state.selectedCountry;
  },
  /** @remarks Control Google Maps */
  metersPerPixal(state: DataState) {
    return function (zoom: number): number {
      if (state.gMaps === undefined) return 0;
      const center: google.maps.LatLng = state.gMaps?.getCenter();
      return (156543.03392 * Math.cos((center.lat() * Math.PI) / 180)) / Math.pow(2, zoom);
    };
  },
  radiusFromCenter() {
    return function (payload: { metersPerPixal: number; windowWidth: number }): number {
      return (payload.windowWidth / 2) * payload.metersPerPixal;
    };
  },
  isPlaceListNoData(state: DataState): boolean {
    return state.placeList.length === 0;
  },
  getLanguageWithoutCountry(state: DataState): string {
    return state.language.substring(0, 2);
  },
  // support language: zh-cn, ja-jp, ko-kr, zh-tw, en-us
  getLanguageWithCountryOnly(state: DataState): string {
    return state.language.substring(3, 5).toLowerCase();
  },
  getDistanceFromCurrentLocation(state: DataState) {
    return function (targetLocation: PortalAdapterApi.Location): string {
      if (state.location.lat === undefined) return "";
      const currentLocation = state.location as PortalAdapterApi.Location;
      // km
      const distance: number = distanceFromLatLng(currentLocation, targetLocation);

      if (distance === -1) return "";

      let displayDistance: string = `${distance}`;
      return displayDistance;
    };
  },
  compareDistanceWithCurrentLocation(state: DataState) {
    return function (location: PortalAdapterApi.Location): number | undefined {
      if (google === undefined) return undefined;
      const currentLocationLatLng: google.maps.LatLng = new google.maps.LatLng(
        state.location.lat as number,
        state.location.lng as number,
      );

      if (currentLocationLatLng.lat == null || currentLocationLatLng.lng == null) {
        return 0;
      }

      const targetLatLng: google.maps.LatLng = new google.maps.LatLng(location.lat, location.lng);
      const distance: number = google.maps.geometry.spherical.computeDistanceBetween(
        targetLatLng,
        currentLocationLatLng,
      );

      return distance;
    };
  },
  compareDistanceWithSelectedTopPlaceLocation(state: DataState) {
    return function (location: PortalAdapterApi.Location): number | undefined {
      if (state?.selectedTopPlace?.location === undefined || google === undefined) return;

      const topPlaceLocationLatLng: google.maps.LatLng = new google.maps.LatLng(
        state?.selectedTopPlace?.location.lat as number,
        state?.selectedTopPlace?.location.lng as number,
      );

      if (topPlaceLocationLatLng.lat == null || topPlaceLocationLatLng.lng == null) {
        return 0;
      }

      const targetLatLng: google.maps.LatLng = new google.maps.LatLng(location.lat, location.lng);
      const distance: number = google.maps.geometry.spherical.computeDistanceBetween(
        targetLatLng,
        topPlaceLocationLatLng,
      );

      return distance;
    };
  },
  getLocationOfPlace(state: DataState) {
    return function (placeIdHash: string): PortalAdapterApi.Location | undefined {
      const selectedPlace: PortalAdapterApi.ResponseGetPublicPlace | undefined = state.sortedPlaceList.find(
        (item: portal.PublicPlaceWithVacancyId) => item.placeIdHash === placeIdHash,
      );

      return selectedPlace !== undefined && selectedPlace.location !== undefined ? selectedPlace.location : undefined;
    };
  },
  getVacancyObjectByPlaceIdHash(state: DataState) {
    return (placeIdHash: string): PortalAdapterApi.ResponseGetMergedDisplayVacancy | undefined => {
      const result: PortalAdapterApi.ResponseGetMergedDisplayVacancy | undefined = state.displayVacancyList.find(
        (item: PortalAdapterApi.ResponseGetMergedDisplayVacancy) => placeIdHash === item.placeIdHash,
      );
      return result !== undefined ? result : undefined;
    };
  },
  getPlaceNameByPlaceIdHashFromSortedList(state: DataState) {
    return (placeIdHash: string): string | undefined => {
      const target: portal.PublicPlaceWithVacancyId | undefined = state.sortedPlaceList.find(
        (place) => place.placeIdHash === placeIdHash,
      );
      return target?.name;
    };
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
